import { useState } from "react";
import { Fragment } from "react";

import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import { Box, IconButton, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { alpha } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { v4 as uuid } from "uuid";

import { createAnswerOptionData } from "../../assets/dataSchema";
import { deepCopyObject } from "../../utils/helpers";
import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTab from "../ui/omniDialogTab";
import DialogTabs from "../ui/omniDialogTabs";
import DialogTitle from "../ui/omniDialogTitle";
import OmniEditButton from "../ui/omniEditButton";
import OmniTextField from "../ui/omniTextField";
import WidgetList from "../ui/widgetList";
import AlertDialog from "./alertDialog";
import PracticeAssessmentTemplateQuestionAnswerOptionDialog from "./practiceAssessmentTemplateQuestionAnswerOptionDialog";

const PracticeAssessmentQuestionTemplateDialog = props => {
  const {
    open,
    dialogTitle = "Add Question",
    okMessage = "OK",
    cancelMessage = "Cancel",
    deleteMessage = "remove",
    maxWidth = "sm",
    fullWidth = true,
    initialValues,
  } = props;

  const [editedValues] = useState(initialValues);
  const [numberText, setNumberText] = useState(initialValues.numberText);
  const [text, setText] = useState(initialValues.text);
  const [subText, setSubText] = useState(initialValues.subText);
  const [helpText, setHelpText] = useState(initialValues.helpText);
  const [instructions, setInstructions] = useState(initialValues.helpText);
  const [answerOptions, setAnswerOptions] = useState(initialValues.answerOptions);

  const [selectedAnswerOption, setSelectedAnswerOption] = useState(null);

  // We don't need to update these values becuase
  // we're creating assessment templates
  const answerValue = initialValues.answerValue;
  const answerDate = initialValues.answerDate;

  const [showConfirm, setShowConfirm] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const onDeleteHandler = () => {
    props.onDelete(initialValues);
  };

  const moveAnswerOption = (from, to) => {
    let reorderedAnswerOptions = deepCopyObject(answerOptions);
    reorderedAnswerOptions.splice(to, 0, reorderedAnswerOptions.splice(from, 1)[0]);

    return reorderedAnswerOptions;
  };

  const removeAnswerOption = value => {
    let updatedAnswerOptions = deepCopyObject(answerOptions);
    updatedAnswerOptions.splice(
      updatedAnswerOptions.findIndex(answerOption => answerOption.value === value),
      1,
    );
    return updatedAnswerOptions;
  };

  const updateAssessmentTemplateQuestionAnswerOptions = updatedAnswerOption => {
    let answerOptionIndex = answerOptions.findIndex(
      answerOption => answerOption.value === updatedAnswerOption.value,
    );

    if (answerOptionIndex >= 0) {
      //We're modifying here
      setAnswerOptions(prevState =>
        prevState.map(answerOption =>
          answerOption.omniID === updatedAnswerOption.omniID
            ? (answerOption = updatedAnswerOption)
            : answerOption,
        ),
      );
    } else {
      setAnswerOptions(prevState => [...prevState, updatedAnswerOption]);
    }
  };

  const onAcceptHandler = () => {
    //notice we didn't touch the id
    editedValues.numberText = numberText;
    editedValues.text = text;
    editedValues.subText = subText;
    editedValues.helpText = helpText;
    editedValues.answerValue = answerValue;
    editedValues.answerDate = answerDate;
    editedValues.answerOptions = answerOptions;
    props.onAccept(editedValues);
  };

  const onCloseHandler = () => {
    props.onClose();
  };

  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogTabs
        tabIndex={tabIndex}
        setTabIndex={setTabIndex}
        variant="fullWidth"
        scrollButtons
        allowScrollButtonsMobile
      >
        <Tab icon={<InfoRoundedIcon />} label="Information" />
        <Tab icon={<QuestionAnswerRoundedIcon />} label="Answer Options" />
      </DialogTabs>
      <DialogContent>
        <DialogTab selectedIndex={tabIndex} index={0}>
          <Divider
            sx={{
              borderRadius: 4,
              borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
              height: 1,
              width: "100%",
              marginBottom: theme.spacing(0.5),
              marginTop: theme.spacing(1),
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              webkitUserSelect: "none",
              userSelect: "none",
              height: 400,
            }}
          >
            <OmniTextField
              label="Question ID"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={numberText}
              onChange={event => {
                setNumberText(event.target.value);
              }}
              placeholder={"Question ID"}
            />

            <OmniTextField
              required
              label="Question Text"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={text}
              onChange={event => {
                setText(event.target.value);
              }}
              placeholder={"Enter the question text"}
            />

            <OmniTextField
              label="Question Subtext"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              value={subText}
              onChange={event => {
                setSubText(event.target.value);
              }}
              placeholder={"Enter the question subtext"}
            />

            <OmniTextField
              label="Help Text"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              multiline
              rows={4}
              value={helpText}
              onChange={event => {
                setHelpText(event.target.value);
              }}
              placeholder={"Enter the instructions for completing the question"}
            />

            <OmniTextField
              label="Instructions"
              type="textInput"
              name={uuid()} //prevents AutoComplete Suggestions
              variant="outlined"
              multiline
              rows={4}
              value={instructions}
              onChange={event => {
                setInstructions(event.target.value);
              }}
              placeholder={"Enter the instructions for completing the question"}
            />
          </Box>
        </DialogTab>

        <DialogTab selectedIndex={tabIndex} index={1}>
          <Divider
            sx={{
              borderRadius: 4,
              borderColor: alpha(theme.palette.commonground.contrastText, 0.25),
              height: 1,
              width: "100%",
              marginBottom: theme.spacing(0.5),
              marginTop: theme.spacing(1),
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              webkitUserSelect: "none",
              userSelect: "none",
              height: 400,
            }}
          >
            <Button
              color="secondary"
              variant="contained"
              onClick={() => setSelectedAnswerOption(createAnswerOptionData)}
            >
              Add Option
            </Button>
            <WidgetList>
              {answerOptions.map((answerOption, index) => {
                return (
                  <ListItem key={`answerOption${index}`} disableGutters={true} dense="true">
                    <ListItemIcon>
                      <Tooltip title={"Move Question Up"} placement="right">
                        <Fragment>
                          <IconButton
                            disabled={index === 0}
                            onClick={e => {
                              e.stopPropagation();
                              setAnswerOptions(moveAnswerOption(index, index - 1));
                            }}
                            size="large"
                          >
                            <KeyboardArrowUpRoundedIcon />
                          </IconButton>
                        </Fragment>
                      </Tooltip>
                    </ListItemIcon>
                    <ListItemIcon>
                      <Tooltip title={"Move Question Down"} placement="right">
                        <Fragment>
                          <IconButton
                            disabled={index === answerOption.length - 1 || index === 0}
                            onClick={e => {
                              e.stopPropagation();
                              setAnswerOptions(moveAnswerOption(index, index + 1));
                            }}
                            size="large"
                          >
                            <KeyboardArrowDownRoundedIcon />
                          </IconButton>
                        </Fragment>
                      </Tooltip>
                    </ListItemIcon>
                    <ListItemIcon>
                      <OmniEditButton
                        size="large"
                        onClick={e => {
                          e.stopPropagation();
                          setAnswerOptions(removeAnswerOption(answerOption.value));
                        }}
                        icon={<DeleteRoundedIcon />}
                      />
                      {/* <Tooltip title={"Delete Question"} placement="right">
                          <IconButton
                            onClick={e => {
                              e.stopPropagation();
                              setAnswerOptions(removeAnswerOption(answerOption.value));
                            }}
                            size="large"
                          >
                            <DeleteRoundedIcon />
                          </IconButton>
                        </Tooltip> */}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body1">
                          {`${answerOption.value}: ${answerOption.text}`}
                        </Typography>
                      }
                      secondary={<Typography variant="body2">{`Text Here`}</Typography>}
                    />
                    <OmniEditButton
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedAnswerOption(answerOption);
                      }}
                    />
                  </ListItem>
                );
              })}
            </WidgetList>
          </Box>
        </DialogTab>
      </DialogContent>

      <DialogActions>
        {initialValues.id != null && (
          <Button
            onClick={() => setShowConfirm(true)}
            sx={{ position: "absolute", left: theme.spacing(2) }}
            color="secondary"
            variant="contained"
          >
            {deleteMessage}
          </Button>
        )}
        {tabIndex === 1 && <Fragment></Fragment>}
        {cancelMessage && (
          <Button onClick={onCloseHandler} color="secondary" variant="contained">
            {cancelMessage}
          </Button>
        )}
        <Button
          onClick={onAcceptHandler}
          color="secondary"
          // disabled={!emailIsValid}
          variant="contained"
        >
          {okMessage}
        </Button>
      </DialogActions>

      {showConfirm && (
        <AlertDialog
          open={showConfirm}
          dialogTitle={`Confirm`}
          dialogMessage={"Are you sure you want to remove this assessment?"}
          onAccept={() => {
            onDeleteHandler();
            setShowConfirm(false);
          }}
          onClose={() => {
            setShowConfirm(false);
          }}
        />
      )}

      {selectedAnswerOption && (
        <PracticeAssessmentTemplateQuestionAnswerOptionDialog
          open={selectedAnswerOption != null}
          dialogTitle={`${selectedAnswerOption.value == null ? "Add" : "Edit"} Answer`}
          initialValues={deepCopyObject(selectedAnswerOption)}
          onDelete={() => {
            setSelectedAnswerOption(null);
          }}
          onAccept={selectedAnswerOption => {
            updateAssessmentTemplateQuestionAnswerOptions(selectedAnswerOption);
            setSelectedAnswerOption(null);
          }}
          onClose={() => {
            setSelectedAnswerOption(null);
          }}
        />
      )}
    </Dialog>
  );
};

export default PracticeAssessmentQuestionTemplateDialog;
